import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Textarea,
  Button,
  useToast,
  VStack,
  Flex,
  Text
} from '@chakra-ui/react';
import { callApi } from '../callApi';

const CaseFeedbackSection = ({ caseId, feedbackType, contextData, accessToken, onSendToChat }) => {
  const [notes, setNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const toast = useToast();

  useEffect(() => {
    const fetchFeedback = async () => {
      if (!caseId || !feedbackType || !accessToken) return;
      
      setIsLoading(true);
      try {
        const response = await callApi(`/api/cases/${caseId}/feedback?feedback_type=${feedbackType}`, accessToken);
        if (response.ok) {
          const data = await response.json();
          if (data.status === 'success' && data.feedback) {
            setNotes(data.feedback.notes || '');
          }
        }
      } catch (error) {
        console.error('Error fetching feedback:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFeedback();
  }, [caseId, feedbackType, accessToken]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const response = await callApi(`/api/cases/${caseId}/feedback`, accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          feedback_type: feedbackType,
          notes,
          context_data: contextData
        })
      });

      if (response.ok) {
        toast({
          title: 'Feedback saved.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        throw new Error('Failed to save feedback');
      }
    } catch (error) {
      console.error('Error saving feedback:', error);
      toast({
        title: 'Error saving feedback.',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return null; // Or a small spinner
  }

  return (
    <Box bg="white" p={4} borderRadius="md" shadow="sm" borderWidth="1px" mb={6}>
      <Heading size="sm" mb={2} color="gray.700">Reviewer Feedback</Heading>
      <Flex gap={4} align="flex-start">
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add feedback about this case..."
          size="sm"
          minHeight="80px"
          flex="1"
        />
        <Button 
          colorScheme="blue" 
          size="sm" 
          onClick={handleSave} 
          isLoading={isSaving}
          px={6}
        >
          Save
        </Button>
        {onSendToChat && (
          <Button 
            colorScheme="teal" 
            variant="outline"
            size="sm" 
            onClick={() => onSendToChat(notes)} 
            isDisabled={!notes.trim()}
            px={6}
          >
            Send Feedback
          </Button>
        )}
      </Flex>
    </Box>
  );
};

export default CaseFeedbackSection;
